import { Col, Row, Space } from 'antd';
import { useFormikContext } from 'formik';
import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type LabelsDataFragment } from '../../../api/fragments/LabelsData';
import { ModuleType } from '../../../api/types';
import { useCompany } from '../../../components/contexts/CompanyContextManager';
import ColorPickerField from '../../../components/fields/ColorPickerField';
import ModuleSelectField from '../../../components/fields/ModuleSelectField';
import CollapsibleWrapper, { Panel } from '../../../components/wrappers/CollapsibleWrapper';
import { useThemeComponents } from '../../../themes';
import useSystemSwitchData from '../../../utilities/useSystemSwitchData';
import { type LabelFormikValue } from './LabelsDetailsInnerPage';
import VehicleModuleManagement from './VehicleModuleManagement';

const colSpan = { lg: 8, md: 12, xs: 24 };

type LabelFormProps = {
    updated?: string;
    label?: LabelsDataFragment;
    offset?: string;
    disabled?: boolean;
};

const LabelForm = ({ updated, label, offset, disabled = false }: LabelFormProps) => {
    const { t } = useTranslation(['labelDetails', 'common']);
    const { values, setFieldValue } = useFormikContext<LabelFormikValue>();
    const company = useCompany(true);
    const { yesNoSwitch } = useSystemSwitchData();
    const { FormFields } = useThemeComponents();

    // To handle initial value if the company only have one module available
    useEffect(() => {
        if (!values.moduleVariants?.length && values.moduleIds?.length === 1) {
            setFieldValue('moduleVariants', [
                ...values.moduleVariants,
                { moduleId: values.moduleIds[0], addedVariantSuiteIds: [] },
            ]);
        }
    }, [setFieldValue, values.moduleIds, values.moduleVariants]);

    const vehicleModules = useMemo(() => values.moduleVariants || [], [values.moduleVariants]);

    const onSelect = useCallback(
        (value, options) => {
            setFieldValue('moduleVariants', [
                ...values.moduleVariants,
                { moduleId: options.value, addedVariantSuiteIds: [] },
            ]);
        },
        [setFieldValue, values.moduleVariants]
    );

    const onDeselect = useCallback(
        value => {
            setFieldValue(
                'moduleVariants',
                values.moduleVariants.filter(moduleVariant => moduleVariant.moduleId !== value)
            );
        },
        [setFieldValue, values.moduleVariants]
    );

    return (
        <Space direction="vertical" style={{ width: '100%' }}>
            <CollapsibleWrapper defaultActiveKey={t('labelDetails:tabs.panel.mainDetails')}>
                <Panel key={t('labelDetails:tabs.panel.mainDetails')} header={t('labelDetails:tabs.panel.mainDetails')}>
                    <Row gutter={14}>
                        <Col {...colSpan}>
                            <FormFields.TranslatedInputField
                                {...t('labelDetails:fields.labelName', { returnObjects: true })}
                                disabled={disabled}
                                maxLength={25}
                                name="labelName"
                                required
                                showCount
                            />
                        </Col>
                        {!updated ? (
                            <Col {...colSpan}>
                                <ModuleSelectField
                                    {...t('labelDetails:fields.labelsModule', { returnObjects: true })}
                                    companyId={company?.id}
                                    disabled={disabled}
                                    moduleTypes={[ModuleType.LabelsModule]}
                                    name="moduleId"
                                    required
                                />
                            </Col>
                        ) : (
                            <Col {...colSpan}>
                                <FormFields.DisplayField
                                    {...t('labelDetails:fields.labelsModule', { returnObjects: true })}
                                    value={label.labelModule.displayName}
                                />
                            </Col>
                        )}

                        <Col {...colSpan}>
                            <ModuleSelectField
                                {...t('labelDetails:fields.module', { returnObjects: true })}
                                companyId={updated ? label.labelModule.companyId : company?.id}
                                disabled={disabled}
                                mode="multiple"
                                moduleTypes={[ModuleType.ConfiguratorModule]}
                                name="moduleIds"
                                onDeselect={onDeselect}
                                onSelect={onSelect}
                                required
                            />
                        </Col>
                        <Col {...colSpan}>
                            <ColorPickerField
                                {...t('labelDetails:fields.backgroundColor', { returnObjects: true })}
                                disabled={disabled}
                                name="backgroundColor"
                                required
                            />
                        </Col>
                        <Col {...colSpan}>
                            <ColorPickerField
                                {...t('labelDetails:fields.textColor', { returnObjects: true })}
                                disabled={disabled}
                                name="textColor"
                                required
                            />
                        </Col>
                        <Col {...colSpan}>
                            <FormFields.SwitchField
                                {...t('labelDetails:fields.isActive', { returnObjects: true })}
                                disabled={disabled}
                                name="isActive"
                                {...yesNoSwitch}
                                required
                            />
                        </Col>
                        {updated && (
                            <Col {...colSpan}>
                                <FormFields.DisplayFieldWithUTCOffset
                                    {...t('common:fields.updated', { returnObjects: true, offset })}
                                    value={updated}
                                />
                            </Col>
                        )}
                    </Row>
                </Panel>
            </CollapsibleWrapper>

            {vehicleModules.map((module, index) => (
                <VehicleModuleManagement
                    key={module.moduleId}
                    disabled={disabled}
                    featureModuleId={module.moduleId}
                    index={index}
                />
            ))}
        </Space>
    );
};

export default LabelForm;
